import React, {
  memo,
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useSnack } from "../../services/SnakbarProvider";

// Helper function to get nested values using dot notation
const getNestedValue = (obj, path) => {
  return path.split('.').reduce((acc, part) => acc && acc[part], obj);
};

const UpdateDropdownEditor = memo(
  forwardRef((props, ref) => {
    // Determine if using new pattern
    const isNewPattern = props.onValueChange && props.stopEditing;
    const { openSnack } = useSnack();

    // Always use params fallback for AG Grid
    const params = props.params || props;
    const valueField = params.valueField || "id";
    const displayField = params.displayField || "name";
    const displaySeparator = params.displaySeparator || " ";
    const options = Array.isArray(params.options) ? params.options : [];
    const initialValue = params.value || "";

    // Get update function and error handler from params
    const updateFunction = params.updateFunction;
    const onError = params.onError;

    // Hooks (always called)
    const [value, setValue] = useState(initialValue);
    const [hasChanged, setHasChanged] = useState(false);
    const [isReady, setIsReady] = useState(false);
    const refContainer = useRef(null);
    const selectRef = useRef(null);

    // AG Grid imperative handle (always called, safe for new pattern too)
    useImperativeHandle(ref, () => ({
      getValue: () => value,
      isPopup: () => true, // Changed to true to help with sizing
      isCancelBeforeStart: () => false,
      isCancelAfterEnd: () => !hasChanged && value === initialValue,
    }));

    // Handle initial mounting
    useEffect(() => {
      const timer = setTimeout(() => {
        setIsReady(true);
      }, 100);

      return () => clearTimeout(timer);
    }, []);

    // Sync value with params
    useEffect(() => {
      setValue(params.value || "");
      setHasChanged(false);
    }, [params.value, params.options, params.valueField, params.displayField]);

    // Focus handling
    useEffect(() => {
      if (isReady && selectRef.current) {
        selectRef.current.focus();
      }
    }, [isReady]);

    // Helper function to get option display label
    const getOptionLabel = (option) => {
      if (Array.isArray(displayField)) {
        return displayField
          .map((field) => {
            if (typeof field === "string" && field.includes(".")) {
              return getNestedValue(option, field) || "";
            }
            return option[field] || "";
          })
          .filter(Boolean) // Remove empty values
          .join(displaySeparator);
      }
      if (typeof displayField === "string" && displayField.includes(".")) {
        return getNestedValue(option, displayField) || option.name || "";
      }
      return option[displayField] || option.name || "";
    };

    // API update handler
    const handleApiUpdate = async (rowId, field, newValue) => {
      console.log("handleApiUpdate", rowId, field, newValue);
      if (!updateFunction) return;

      try {
        await updateFunction(rowId, {
          [field]: newValue,
        });

        openSnack("Actualización exitosa. Refresque la página para ver los cambios.", "success");

        // Optional: you might want to refresh the grid data here
        if (params.api?.refreshCells) {
          params.api.refreshCells({
            rowNodes: [params.node],
            columns: [params.column.colId],
            force: true,
          });
        }
      } catch (error) {
        console.error("Error updating value:", error);
        openSnack("Error al actualizar el valor.", "error");
        if (onError && typeof onError === "function") {
          onError(error);
        }
      }
    };

    // Handlers
    const handleChange = async (event) => {
      console.log("handleChange", event.target.value);
      // Convert the value to number if it's numeric
      const newValue = event.target.value === "" ? null : 
        (!isNaN(event.target.value) ? Number(event.target.value) : event.target.value);
      
      setValue(newValue);
      setHasChanged(true);

      if (updateFunction) {
        await handleApiUpdate(
          params.data.id, // Row ID
          params.column.colId, // Column field name
          newValue // New value
        );
        
      }

      if (isNewPattern) {
        props.onValueChange(newValue);
      } else if (params.api && params.node && params.column) {
        try {
          // Update AG Grid value
          params.api.setEditCellValue({
            rowIndex: params.node.rowIndex,
            colKey: params.column.colId,
            value: newValue,
          });
        } catch (error) {
          if (onError && typeof onError === "function") {
            onError(error);
          }
        }
      }

      // AG Grid: stop editing if available
      if (typeof params.stopEditing === "function") {
        params.stopEditing();
      }
    };

    const handleKeyDown = (event) => {
      if (isNewPattern) {
        if (event.key === "Enter" || event.key === "Escape") {
          props.stopEditing();
          event.stopPropagation();
        }
      }
    };

    if (!isReady) {
      return null;
    }

    // Render
    return (
      <div
        ref={refContainer}
        style={{
          position: "absolute",
          width: "200px", // Fixed width to prevent resize issues
          height: "auto",
          backgroundColor: "white",
          zIndex: 1000,
        }}
      >
        <select
          ref={selectRef}
          value={value || ""}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          style={{
            width: "100%",
            padding: "4px 8px",
            border: "1px solid #ccc",
            borderRadius: "4px",
            fontSize: "14px",
            backgroundColor: "white",
            outline: "none",
          }}
        >
          <option value="">Seleccionar</option>
          {options.map((option, index) => {
            const optionValue =
              option[valueField] !== undefined
                ? option[valueField]
                : option.id || option.value || index;
            const optionDisplay = getOptionLabel(option) || `Option ${index + 1}`;
            return (
              <option key={optionValue || index} value={optionValue}>
                {optionDisplay}
              </option>
            );
          })}
        </select>
      </div>
    );
  })
);

UpdateDropdownEditor.displayName = "UpdateDropdownEditor";
export default UpdateDropdownEditor;
